import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createFaqCategoryAsync, deleteFaqCategoryAsync, getFaqCategoriesByEventIdAsync, reorderFaqCategoriesAsync } from '@application/features/faqCategory/faqCategoryThunk';
import { createFaqQuestionAsync, deleteFaqQuestionAsync, getFaqQuestionsByCategoryIdAsync, reorderFaqQuestionsAsync, updateFaqQuestionAsync, updateFaqQuestionWithCategoryAsync } from '@application/features/faqQuestion/faqQuestionThunk';
import { createFamilyFestFaqCategoryAsync, deleteFamilyFestFaqCategoryAsync, getFamilyFestFaqCategoriesByTypeAsync, reorderFamilyFestFaqCategoriesAsync } from '@application/features/familyFestFaqCategory/familyFestFaqCategoryThunk';
import { createFamilyFestFaqQuestionAsync, deleteFamilyFestFaqQuestionAsync, getFamilyFestFaqQuestionsByCategoryIdAsync, reorderFamilyFestFaqQuestionsAsync, updateFamilyFestFaqQuestionAsync } from '@application/features/familyFestFaqQuestion/familyFestFaqQuestionThunk';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { FamilyFestFaqTypeEnum, faqTypeOfCategory } from '@application/enums/family-fest-faq-type-enum';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Button, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Add, ArrowLeft2 } from 'iconsax-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import DetailsContent from './DetailsContent';
import FaqsContent from './FaqsContent';
import FaqsDetails from './FaqsContent/FaqsDetails';
import FaqTypeFilter from './FaqTypeFilter';
import NewCategoryDialog from './NewCategoryDialog';
import SortableCategoryRowItem from './SortableCategoryRowItem';
import { toast } from 'react-toastify';
import { getEventLanguagesByEventIdAsync } from '@application/features/languages/languagesThunk';
const TabPanel = ({ children, value, index }) => {
    if (value !== index)
        return null;
    return (_jsx(Box, { role: 'tabpanel', hidden: value !== index, id: `faqs-inner-tabpanel-${index}`, children: value === index && _jsx(Box, { children: children }) }));
};
// FamilyFest categories also carry familyFestFaqType; standard event categories don't.
const mapToCategoryItems = (items) => items.map((category) => ({
    id: category.id,
    name: category.name,
    questionCount: category.questionsCount ?? 0,
    translations: category.translations?.map(t => ({ languageCode: t.languageCode, name: t.name })) ?? [],
    familyFestFaqType: category.familyFestFaqType
}));
const mapToFaqItems = (items) => items.map((faq) => ({
    id: faq.id,
    question: faq.question,
    answer: faq.answer,
    sortOrder: faq.sortOrder,
    faqCategoryId: faq.faqCategoryId,
    translations: faq.translations ?? [],
    isLoading: false
}));
const withFaqSortOrder = (items) => items.map((item, index) => ({
    ...item,
    sortOrder: index + 1
}));
const FaqsTab = ({ onOpenFaqDetails }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'));
    const { VDKEvents, VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    const currentEvent = VDKEventsForEventDetails || (VDKEvents ?? []).find((e) => e.id === eventId);
    const isFamilyEvent = currentEvent?.type === EventTypeEnum.Family;
    const faqCategories = useAppSelector((state) => isFamilyEvent ? state.familyFestFaqCategory.categories : state.faqCategory.categories);
    const faqQuestions = useAppSelector((state) => isFamilyEvent ? state.familyFestFaqQuestion.faqQuestions : state.faqQuestion.questions);
    const [activeTab, setActiveTab] = useState(0);
    const [openAddCategoryDialog, setOpenAddCategoryDialog] = useState(false);
    const [categories, setCategories] = useState([]);
    const [activeCategoryId, setActiveCategoryId] = useState('');
    const [faqItems, setFaqItems] = useState([]);
    const [faqSearchValue, setFaqSearchValue] = useState('');
    const [deleteCategoryOpen, setDeleteCategoryOpen] = useState(false);
    const [categoryIdToDelete, setCategoryIdToDelete] = useState(null);
    // FamilyFest: the list shows one FAQ type at a time (registration side or event page).
    const [selectedFaqType, setSelectedFaqType] = useState(FamilyFestFaqTypeEnum.Registration);
    // Set while a category moves to the other type, so the selection waits for the refreshed list.
    const followCategoryIdRef = useRef(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));
    // Mobile navigation state: 'categories' | 'faqs' | 'faqDetails'
    const [mobileView, setMobileView] = useState('categories');
    const [mobileFaqDetailsTab, setMobileFaqDetailsTab] = useState(0);
    const [selectedMobileFaq, setSelectedMobileFaq] = useState(null);
    // FamilyFest: the backend returns only the selected type's categories (get-by-type).
    useEffect(() => {
        if (!eventId)
            return;
        if (isFamilyEvent) {
            dispatch(getFamilyFestFaqCategoriesByTypeAsync({ familyFestFaqType: selectedFaqType }));
        }
        else {
            dispatch(getFaqCategoriesByEventIdAsync({ eventId }));
        }
    }, [dispatch, eventId, isFamilyEvent, selectedFaqType]);
    const allCategoryItems = useMemo(() => mapToCategoryItems(faqCategories), [faqCategories]);
    // Guard only: the list is already filtered by the backend, but another screen may have loaded
    // every category into the store (get-all) in the meantime.
    useEffect(() => {
        setCategories(isFamilyEvent
            ? allCategoryItems.filter((category) => faqTypeOfCategory(category) === selectedFaqType)
            : allCategoryItems);
    }, [allCategoryItems, isFamilyEvent, selectedFaqType]);
    useEffect(() => {
        const activeCategoryStillExists = categories.some((item) => item.id === activeCategoryId);
        if (activeCategoryStillExists) {
            if (followCategoryIdRef.current === activeCategoryId)
                followCategoryIdRef.current = null;
            return;
        }
        // A category that just moved to this type shows up once the list is refreshed.
        if (activeCategoryId && followCategoryIdRef.current === activeCategoryId)
            return;
        setActiveCategoryId(categories[0]?.id ?? '');
    }, [categories, activeCategoryId]);
    useEffect(() => {
        setFaqItems(mapToFaqItems(faqQuestions));
    }, [faqQuestions]);
    useEffect(() => {
        if (!activeCategoryId) {
            setFaqItems([]);
            return;
        }
        setFaqItems([]);
        if (isFamilyEvent) {
            dispatch(getFamilyFestFaqQuestionsByCategoryIdAsync({
                faqCategoryId: activeCategoryId,
                searchValue: faqSearchValue.trim() || undefined
            }));
        }
        else {
            dispatch(getFaqQuestionsByCategoryIdAsync({
                faqCategoryId: activeCategoryId,
                searchValue: faqSearchValue.trim() || undefined
            }));
        }
    }, [dispatch, activeCategoryId, faqSearchValue, isFamilyEvent]);
    useEffect(() => {
        if (!eventId)
            return;
        dispatch(getEventLanguagesByEventIdAsync(eventId));
    }, [dispatch, eventId]);
    const categoryToDelete = categories.find((c) => c.id === categoryIdToDelete) ?? null;
    const handleTabChange = (_event, newValue) => {
        setActiveTab(newValue);
    };
    const persistCategoryOrder = async (previousCategories, reorderedCategories) => {
        if (!eventId)
            return;
        try {
            let response;
            if (isFamilyEvent) {
                response = await dispatch(reorderFamilyFestFaqCategoriesAsync({
                    items: reorderedCategories.map((item, index) => ({
                        id: item.id,
                        sortOrder: index + 1
                    }))
                })).unwrap();
            }
            else {
                response = await dispatch(reorderFaqCategoriesAsync({
                    eventId,
                    items: reorderedCategories.map((item, index) => ({
                        id: item.id,
                        sortOrder: index + 1
                    }))
                })).unwrap();
            }
            if (!response?.isSuccess) {
                throw new Error(response?.message);
            }
            if (isFamilyEvent) {
                refreshCategories();
            }
            else {
                dispatch(getFaqCategoriesByEventIdAsync({ eventId }));
            }
        }
        catch {
            setCategories(previousCategories);
            toast.error(t('events.eventDetails.faqTab.failedtoupdateCategory'));
        }
    };
    const persistFaqOrder = async (previousFaqs, reorderedFaqs) => {
        if (!activeCategoryId)
            return;
        try {
            let response;
            if (isFamilyEvent) {
                response = await dispatch(reorderFamilyFestFaqQuestionsAsync({
                    faqCategoryId: activeCategoryId,
                    items: reorderedFaqs.map((item, index) => ({
                        id: item.id,
                        sortOrder: index + 1
                    }))
                })).unwrap();
            }
            else {
                response = await dispatch(reorderFaqQuestionsAsync({
                    faqCategoryId: activeCategoryId,
                    items: reorderedFaqs.map((item, index) => ({
                        id: item.id,
                        sortOrder: index + 1
                    }))
                })).unwrap();
            }
            if (!response?.isSuccess) {
                toast.error(response?.message);
            }
            if (isFamilyEvent) {
                dispatch(getFamilyFestFaqQuestionsByCategoryIdAsync({
                    faqCategoryId: activeCategoryId,
                    searchValue: faqSearchValue.trim() || undefined
                }));
            }
            else {
                dispatch(getFaqQuestionsByCategoryIdAsync({
                    faqCategoryId: activeCategoryId,
                    searchValue: faqSearchValue.trim() || undefined
                }));
            }
        }
        catch {
            setFaqItems(previousFaqs);
            toast.error(t('events.eventDetails.faqTab.failedToUpdateQuestion'));
        }
    };
    const handleCategoryDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        const oldIndex = categories.findIndex((item) => item.id === active.id);
        const newIndex = categories.findIndex((item) => item.id === over.id);
        if (oldIndex < 0 || newIndex < 0)
            return;
        const previousCategories = categories;
        const reorderedCategories = arrayMove(categories, oldIndex, newIndex);
        setCategories(reorderedCategories);
        void persistCategoryOrder(previousCategories, reorderedCategories);
    };
    const activeCategory = categories.find((item) => item.id === activeCategoryId) ?? categories[0];
    const handleCreateCategory = ({ name, familyFestFaqType }) => {
        const trimmed = name.trim();
        if (!trimmed || !eventId)
            return;
        const dispatchAction = isFamilyEvent
            ? dispatch(createFamilyFestFaqCategoryAsync({ name: trimmed, familyFestFaqType }))
            : dispatch(createFaqCategoryAsync({ name: trimmed, eventId }));
        dispatchAction
            .unwrap()
            .then((response) => {
            if (response?.isSuccess) {
                const createdCategory = response.data;
                if (!createdCategory)
                    return;
                if (isFamilyEvent) {
                    // Show the list the new category belongs to, with it selected.
                    followCategoryIdRef.current = createdCategory.id;
                    setSelectedFaqType(familyFestFaqType);
                }
                setActiveCategoryId(createdCategory.id);
                setOpenAddCategoryDialog(false);
                toast.success(response?.message || t('events.eventDetails.faqTab.categoryCreatedSuccessfully'));
            }
            else {
                toast.error(response?.message || t('events.eventDetails.faqTab.failedtocreateCategory'));
            }
        })
            .catch(() => { });
    };
    const handleCategoryFaqsChange = (nextFaqs) => {
        if (faqSearchValue.trim()) {
            toast.error(t('events.eventDetails.faqTab.failedToUpdateQuestion'));
            return;
        }
        const previousFaqs = faqItems;
        const reorderedFaqs = withFaqSortOrder(nextFaqs);
        setFaqItems(reorderedFaqs);
        void persistFaqOrder(previousFaqs, reorderedFaqs);
    };
    const handleRequestDeleteCategory = (id) => {
        setCategoryIdToDelete(id);
        setDeleteCategoryOpen(true);
    };
    const handleConfirmDeleteCategory = () => {
        if (!categoryIdToDelete)
            return;
        const dispatchAction = isFamilyEvent
            ? dispatch(deleteFamilyFestFaqCategoryAsync({ id: categoryIdToDelete }))
            : dispatch(deleteFaqCategoryAsync({ id: categoryIdToDelete }));
        dispatchAction
            .unwrap()
            .then((response) => {
            if (response?.isSuccess) {
                toast.success(response.message || t('events.eventDetails.faqTab.categoryDeletedSuccessfully'));
            }
            else {
                toast.error(response.message || t('events.eventDetails.faqTab.failedToDeleteCategory'));
            }
        });
        setDeleteCategoryOpen(false);
        setCategoryIdToDelete(null);
    };
    const handleCreateFaq = (payload) => {
        const targetCategoryId = payload.category?.value || activeCategory?.id;
        if (!targetCategoryId)
            return Promise.resolve(false);
        const targetCategory = categories.find((item) => item.id === targetCategoryId);
        const validFaqItems = payload.faqItems.filter((item) => item.question.trim() !== '' && item.answer.trim() !== '');
        if (validFaqItems.length === 0)
            return Promise.resolve(false);
        const mappedQuestions = validFaqItems.map((item, index) => ({
            question: item.question.trim(),
            answer: item.answer.trim(),
            sortOrder: (targetCategory?.questionCount ?? 0) + index + 1,
            faqCategoryId: targetCategoryId
        }));
        const dispatchAction = isFamilyEvent
            ? dispatch(createFamilyFestFaqQuestionAsync({ questions: mappedQuestions }))
            : dispatch(createFaqQuestionAsync({ questions: mappedQuestions }));
        return dispatchAction
            .unwrap()
            .then((response) => {
            if (response?.isSuccess) {
                if (eventId) {
                    if (isFamilyEvent) {
                        refreshCategories();
                    }
                    else {
                        dispatch(getFaqCategoriesByEventIdAsync({ eventId }));
                    }
                }
                if (activeCategoryId === targetCategoryId) {
                    if (isFamilyEvent) {
                        dispatch(getFamilyFestFaqQuestionsByCategoryIdAsync({
                            faqCategoryId: targetCategoryId,
                            searchValue: faqSearchValue.trim() || undefined
                        }));
                    }
                    else {
                        dispatch(getFaqQuestionsByCategoryIdAsync({
                            faqCategoryId: targetCategoryId,
                            searchValue: faqSearchValue.trim() || undefined
                        }));
                    }
                }
                toast.success(response.message || t('events.eventDetails.faqTab.questionCreatedSuccessfully'));
                return true;
            }
            toast.error(response.message ||
                t('events.eventDetails.faqTab.failedtocreatequestionSuccessfully'));
            return false;
        })
            .catch(() => false);
    };
    // Category rows show questionsCount from the category list, so reload it whenever a FAQ may
    // have moved to another category (otherwise both rows keep their old counts).
    const refreshCategories = () => {
        if (isFamilyEvent) {
            dispatch(getFamilyFestFaqCategoriesByTypeAsync({ familyFestFaqType: selectedFaqType }));
        }
        else if (eventId) {
            dispatch(getFaqCategoriesByEventIdAsync({ eventId }));
        }
    };
    const handleSaveFaq = (payload) => {
        if (!payload.categoryId)
            return Promise.resolve(false);
        const targetFaq = faqItems.find((f) => f.id === payload.id);
        let dispatchAction;
        if (isFamilyEvent) {
            dispatchAction = dispatch(updateFamilyFestFaqQuestionAsync({
                id: payload.id,
                question: payload.question.trim(),
                answer: payload.answer.trim(),
                sortOrder: targetFaq?.sortOrder ?? 0,
                faqCategoryId: payload.categoryId,
                translations: targetFaq?.translations?.map((t) => ({
                    languageId: t.languageId,
                    question: t.question,
                    answer: t.answer
                })) ?? []
            }));
        }
        else {
            dispatchAction = dispatch(updateFaqQuestionWithCategoryAsync({
                id: payload.id,
                question: payload.question.trim(),
                answer: payload.answer.trim(),
                categoryId: payload.categoryId
            }));
        }
        return dispatchAction
            .unwrap()
            .then((response) => {
            if (response?.isSuccess) {
                toast.success(response.message || t('events.eventDetails.faqTab.questionUpdatedSuccessfully'));
                refreshCategories();
                if (activeCategoryId) {
                    if (isFamilyEvent) {
                        dispatch(getFamilyFestFaqQuestionsByCategoryIdAsync({
                            faqCategoryId: activeCategoryId,
                            searchValue: faqSearchValue.trim() || undefined
                        }));
                    }
                    else {
                        dispatch(getFaqQuestionsByCategoryIdAsync({
                            faqCategoryId: activeCategoryId,
                            searchValue: faqSearchValue.trim() || undefined
                        }));
                    }
                }
                return true;
            }
            toast.error(response.message || t('events.eventDetails.faqTab.failedToUpdateQuestion'));
            return false;
        })
            .catch(() => {
            toast.error(t('events.eventDetails.faqTab.failedToUpdateQuestion'));
            return false;
        });
    };
    const handleDeleteFaq = (faqId) => {
        const dispatchAction = isFamilyEvent
            ? dispatch(deleteFamilyFestFaqQuestionAsync(faqId))
            : dispatch(deleteFaqQuestionAsync({ id: faqId }));
        return dispatchAction
            .unwrap()
            .then((response) => {
            if (response?.isSuccess) {
                if (eventId) {
                    if (isFamilyEvent) {
                        refreshCategories();
                    }
                    else {
                        dispatch(getFaqCategoriesByEventIdAsync({ eventId }));
                    }
                }
                if (activeCategoryId) {
                    if (isFamilyEvent) {
                        dispatch(getFamilyFestFaqQuestionsByCategoryIdAsync({
                            faqCategoryId: activeCategoryId,
                            searchValue: faqSearchValue.trim() || undefined
                        }));
                    }
                    else {
                        dispatch(getFaqQuestionsByCategoryIdAsync({
                            faqCategoryId: activeCategoryId,
                            searchValue: faqSearchValue.trim() || undefined
                        }));
                    }
                }
                toast.success(response.message || t('events.eventDetails.faqTab.questionDeletedSuccessfully'));
                return true;
            }
            toast.error(response.message ||
                t('events.eventDetails.faqTab.failedtodeletequestionSuccessfully'));
            return false;
        })
            .catch(() => false);
    };
    const handleSaveFaqFromDetails = (payload) => {
        const request = {
            id: payload.id,
            question: payload.question,
            answer: payload.answer,
            sortOrder: payload.sortOrder,
            faqCategoryId: payload.faqCategoryId,
            translations: payload.translations
        };
        const dispatchAction = isFamilyEvent
            ? dispatch(updateFamilyFestFaqQuestionAsync(request))
            : dispatch(updateFaqQuestionAsync(request));
        return dispatchAction
            .unwrap()
            .then(() => {
            toast.success(t('events.eventDetails.faqTab.questionUpdatedSuccessfully'));
            refreshCategories();
            if (activeCategoryId) {
                if (isFamilyEvent) {
                    dispatch(getFamilyFestFaqQuestionsByCategoryIdAsync({
                        faqCategoryId: activeCategoryId,
                        searchValue: faqSearchValue.trim() || undefined
                    }));
                }
                else {
                    dispatch(getFaqQuestionsByCategoryIdAsync({
                        faqCategoryId: activeCategoryId,
                        searchValue: faqSearchValue.trim() || undefined
                    }));
                }
            }
            return true;
        })
            .catch(() => {
            toast.error(t('events.eventDetails.faqTab.failedToUpdateQuestion'));
            return false;
        });
    };
    // FamilyFest FAQ dialogs fetch the chosen type's categories themselves (useFamilyFestFaqCategoryOptions).
    const categoryOptions = useMemo(() => categories.map((item) => ({ label: item.name, value: item.id })), [categories]);
    const handleCategoryFaqTypeChanged = (faqType) => {
        followCategoryIdRef.current = activeCategoryId;
        setSelectedFaqType(faqType);
    };
    const faqTypeFilter = isFamilyEvent ? (_jsx(FaqTypeFilter, { value: selectedFaqType, onChange: setSelectedFaqType })) : null;
    const handleMobileCategorySelect = (id) => {
        setActiveCategoryId(id);
        if (isBelowLg) {
            setMobileView('faqs');
        }
    };
    const handleMobileFaqSelect = (faq) => {
        if (isBelowLg) {
            setSelectedMobileFaq(faq);
            setMobileFaqDetailsTab(0);
            setMobileView('faqDetails');
        }
        else {
            onOpenFaqDetails?.({ faq, categoryName: activeCategory?.name || '' });
        }
    };
    const handleMobileBack = () => {
        if (mobileView === 'faqDetails') {
            setSelectedMobileFaq(null);
            setMobileView('faqs');
        }
        else if (mobileView === 'faqs') {
            setMobileView('categories');
        }
    };
    // --- Mobile Layout (below lg) ---
    if (isBelowLg) {
        return (_jsxs(_Fragment, { children: [mobileView === 'categories' && (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.faqTab.categories') }), _jsx(Button, { variant: 'contained', color: 'primary', startIcon: _jsx(Add, { variant: 'Outline' }), onClick: () => setOpenAddCategoryDialog(true), children: t('events.eventDetails.faqTab.newCategory') })] }), faqTypeFilter, _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, modifiers: [restrictToVerticalAxis, restrictToParentElement], onDragEnd: handleCategoryDragEnd, children: _jsx(SortableContext, { items: categories.map((item) => item.id), strategy: verticalListSortingStrategy, children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: categories.map((item) => (_jsx(SortableCategoryRowItem, { item: item, selected: activeCategory?.id === item.id, onSelect: handleMobileCategorySelect, onDelete: handleRequestDeleteCategory }, item.id))) }) }) })] })), mobileView === 'faqs' && (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.5, onClick: handleMobileBack, sx: { cursor: 'pointer' }, children: [_jsx(ArrowLeft2, { size: 18 }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.faqTab.categories') })] }), _jsx(Typography, { variant: 'h5', children: activeCategory?.name })] }), _jsxs(Box, { borderRadius: 4, padding: '0px 16px 16px 16px', display: 'flex', flexDirection: 'column', gap: 2, boxShadow: theme.customShadows.card, children: [_jsx(Box, { pt: 2, width: 'max-content', children: _jsxs(Tabs, { value: activeTab, onChange: handleTabChange, variant: 'scrollable', scrollButtons: 'auto', sx: {
                                            '& .MuiTab-root': {
                                                minHeight: 48,
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            },
                                            '& .MuiTab-root:not(.Mui-selected)': {
                                                color: theme.palette.text.primary
                                            },
                                            '& .MuiTab-root.Mui-selected': {
                                                color: theme.palette.text.primary
                                            }
                                        }, children: [_jsx(Tab, { label: t('events.eventDetails.faqTab.faqs') }), _jsx(Tab, { label: t('events.eventDetails.faqTab.details') })] }) }), _jsx(TabPanel, { value: activeTab, index: 0, children: _jsx(FaqsContent, { isFamilyEvent: isFamilyEvent, faqItems: faqItems, categories: categoryOptions, defaultFaqType: selectedFaqType, searchValue: faqSearchValue, onSearchChange: setFaqSearchValue, onFaqItemsChange: handleCategoryFaqsChange, onCreateFaq: handleCreateFaq, onDeleteFaq: handleDeleteFaq, onSaveFaq: handleSaveFaq, onFaqSelect: handleMobileFaqSelect }) }), _jsx(TabPanel, { value: activeTab, index: 1, children: _jsx(DetailsContent, { category: activeCategory, isFamilyEvent: isFamilyEvent, onFaqTypeChanged: handleCategoryFaqTypeChanged, onCategorySaved: refreshCategories }) })] })] })), mobileView === 'faqDetails' && selectedMobileFaq && (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.5, onClick: handleMobileBack, sx: { cursor: 'pointer' }, children: [_jsx(ArrowLeft2, { size: 18 }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.faqTab.categories') })] }), _jsx(Typography, { variant: 'h5', children: activeCategory?.name })] }), _jsxs(Box, { borderRadius: 4, padding: '0px 16px 16px 16px', display: 'flex', flexDirection: 'column', gap: 2, boxShadow: theme.customShadows.card, children: [_jsx(Box, { pt: 2, width: 'max-content', children: _jsxs(Tabs, { value: mobileFaqDetailsTab, onChange: (_e, v) => setMobileFaqDetailsTab(v), variant: 'scrollable', scrollButtons: 'auto', sx: {
                                            '& .MuiTab-root': {
                                                minHeight: 48,
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            },
                                            '& .MuiTab-root:not(.Mui-selected)': {
                                                color: theme.palette.text.primary
                                            },
                                            '& .MuiTab-root.Mui-selected': {
                                                color: theme.palette.text.primary
                                            }
                                        }, children: [_jsx(Tab, { label: t('events.eventDetails.faqTab.faqDetails') }), _jsx(Tab, { label: t('events.eventDetails.faqTab.translations') })] }) }), _jsx(FaqsDetails, { faq: selectedMobileFaq, categoryName: selectedMobileFaq.faqCategoryId, visibleSection: mobileFaqDetailsTab === 0 ? 'details' : 'translations', onDeleteFaq: handleDeleteFaq, onSaveFaq: handleSaveFaqFromDetails, onBack: handleMobileBack })] })] })), _jsx(NewCategoryDialog, { open: openAddCategoryDialog, onClose: () => setOpenAddCategoryDialog(false), onCreate: handleCreateCategory, isFamilyEvent: isFamilyEvent, defaultFaqType: selectedFaqType }), _jsx(ConfirmationDialog, { open: deleteCategoryOpen, onClose: () => {
                        setDeleteCategoryOpen(false);
                        setCategoryIdToDelete(null);
                    }, title: t('events.eventDetails.faqTab.deleteCategory') || 'Delete Category', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                    setDeleteCategoryOpen(false);
                                    setCategoryIdToDelete(null);
                                }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'error', onClick: handleConfirmDeleteCategory, children: t('actions.deleteNow') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.eventDetails.faqTab.dialog.deleteCategoryConfirmation', {
                                categoryName: categoryToDelete?.name ?? ''
                            }) }) }) })] }));
    }
    return (_jsxs(Grid, { container: true, columnSpacing: 2.5, children: [_jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.faqTab.categories') }), _jsx(Button, { variant: 'contained', color: 'primary', startIcon: _jsx(Add, { variant: 'Outline' }), onClick: () => setOpenAddCategoryDialog(true), children: t('events.eventDetails.faqTab.newCategory') })] }), faqTypeFilter, _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, modifiers: [restrictToVerticalAxis, restrictToParentElement], onDragEnd: handleCategoryDragEnd, children: _jsx(SortableContext, { items: categories.map((item) => item.id), strategy: verticalListSortingStrategy, children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: categories.map((item) => (_jsx(SortableCategoryRowItem, { item: item, selected: activeCategory?.id === item.id, onSelect: setActiveCategoryId, onDelete: handleRequestDeleteCategory }, item.id))) }) }) })] }) }), _jsx(Grid, { size: { xs: 12, md: 9 }, pb: 1, children: _jsxs(Box, { borderRadius: 4, padding: '0px 16px 16px 16px', display: 'flex', flexDirection: 'column', gap: 2, boxShadow: theme.customShadows.card, children: [_jsx(Box, { pt: 2, width: 'max-content', children: _jsxs(Tabs, { value: activeTab, onChange: handleTabChange, variant: 'scrollable', scrollButtons: 'auto', sx: {
                                    '& .MuiTab-root': {
                                        minHeight: 48,
                                        textTransform: 'none',
                                        fontSize: '14px',
                                        fontWeight: 500
                                        // Non-active tab color
                                    },
                                    '& .MuiTab-root:not(.Mui-selected)': {
                                        color: theme.palette.text.primary
                                    },
                                    '& .MuiTab-root.Mui-selected': {
                                        color: theme.palette.text.primary // Active tab color
                                    }
                                }, children: [_jsx(Tab, { label: t('events.eventDetails.faqTab.faqs') }), _jsx(Tab, { label: t('events.eventDetails.faqTab.details') })] }) }), _jsx(TabPanel, { value: activeTab, index: 0, children: _jsx(FaqsContent, { isFamilyEvent: isFamilyEvent, faqItems: faqItems, categories: categoryOptions, defaultFaqType: selectedFaqType, searchValue: faqSearchValue, onSearchChange: setFaqSearchValue, onFaqItemsChange: handleCategoryFaqsChange, onCreateFaq: handleCreateFaq, onDeleteFaq: handleDeleteFaq, onSaveFaq: handleSaveFaq, onFaqSelect: (faq) => onOpenFaqDetails?.({ faq, categoryName: activeCategory?.name || '' }) }) }), _jsx(TabPanel, { value: activeTab, index: 1, children: _jsx(DetailsContent, { category: activeCategory, isFamilyEvent: isFamilyEvent, onFaqTypeChanged: handleCategoryFaqTypeChanged, onCategorySaved: refreshCategories }) })] }) }), _jsx(NewCategoryDialog, { open: openAddCategoryDialog, onClose: () => setOpenAddCategoryDialog(false), onCreate: handleCreateCategory, isFamilyEvent: isFamilyEvent, defaultFaqType: selectedFaqType }), _jsx(ConfirmationDialog, { open: deleteCategoryOpen, onClose: () => {
                    setDeleteCategoryOpen(false);
                    setCategoryIdToDelete(null);
                }, title: t('events.eventDetails.faqTab.deleteCategory') || 'Delete Category', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setDeleteCategoryOpen(false);
                                setCategoryIdToDelete(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'error', onClick: handleConfirmDeleteCategory, children: t('actions.deleteNow') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.eventDetails.faqTab.dialog.deleteCategoryConfirmation', {
                            categoryName: categoryToDelete?.name ?? ''
                        }) }) }) })] }));
};
export default FaqsTab;
